import React, { useCallback, useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import Hls from 'hls.js';
import { Loader2, Minus, Plus, RotateCcw, X } from 'lucide-react';
import { pedirSesion, consultarPlaylist, soportaHevc } from './vivoApi';
import { SIN_ZOOM, ZOOM_MAX, factorRueda, mover, zoomEn } from './zoomPan';

/* Botón rojo "VIVO beta" sobre el timelapse de cada cámara + reproductor del vivo real (Neolink) con zoom:
   pellizco con dos dedos en el celular, rueda del mouse en la PC, arrastrar para moverse y doble clic/toque para
   acercar. El servidor despierta la cámara recién cuando alguien toca el botón y la corta a los 2 minutos
   (pedido de Jairo 30/10/2026: "en todas las cámaras un botón VIVO en rojo que diga beta"). */

const POLL_MS = 2000;
const MAIN_HASTA_MS = 65000;   // si la 4K no arranca en este tiempo, se pide la señal chica (h264)
const RENDIRSE_MS = 120000;    // despertar una 4G con batería tarda 30-45 s; pasado esto, no responde

const formatear = (seg) => `${Math.floor(seg / 60)}:${String(Math.max(0, seg % 60)).padStart(2, '0')}`;

export const BotonVivo = ({ onClick }) => (
  <button
    type="button"
    onClick={onClick}
    aria-label="Ver la cámara en vivo (beta)"
    className="inline-flex items-center gap-1.5 rounded-full bg-red-600 pl-2.5 pr-1.5 py-1 text-white shadow-lg ring-1 ring-white/40 hover:bg-red-700 active:scale-95 transition"
    data-vivo-boton
  >
    <span className="relative flex h-2.5 w-2.5">
      <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-white opacity-70" />
      <span className="relative inline-flex h-2.5 w-2.5 rounded-full bg-white" />
    </span>
    <span className="text-sm font-extrabold tracking-wider">VIVO</span>
    <span className="rounded-full bg-white/25 px-1.5 py-0.5 text-[10px] font-semibold lowercase leading-none">beta</span>
  </button>
);

// Marco con zoom: todo el gesto se maneja con pointer events (sirve igual para mouse, dedo y lápiz).
const MarcoZoom = ({ children, zoom, setZoom }) => {
  const marco = useRef(null);
  const punteros = useRef(new Map());
  const pinza = useRef(null);
  const zoomRef = useRef(zoom);
  zoomRef.current = zoom;
  // los gestos llegan más rápido que los renders (touchpad, dos dedos): el último zoom se guarda al toque en el ref
  const aplicar = useCallback((t) => { zoomRef.current = t; setZoom(t); }, [setZoom]);

  const medidas = () => {
    const r = marco.current.getBoundingClientRect();
    return { r, w: r.width, h: r.height };
  };

  useEffect(() => {
    const el = marco.current;
    // la rueda tiene que poder cancelar el scroll de la página: listener no pasivo
    const rueda = (e) => {
      e.preventDefault();
      const { r, w, h } = medidas();
      const t = zoomRef.current;
      aplicar(zoomEn(t, e.clientX - r.left, e.clientY - r.top, t.s * factorRueda(e.deltaY), w, h));
    };
    el.addEventListener('wheel', rueda, { passive: false });
    return () => el.removeEventListener('wheel', rueda);
  }, [aplicar]);

  const abajo = (e) => {
    try { marco.current.setPointerCapture(e.pointerId); } catch (err) { /* sin captura igual anda el gesto */ }
    punteros.current.set(e.pointerId, { x: e.clientX, y: e.clientY });
    pinza.current = null;
  };

  const movido = (e) => {
    const previo = punteros.current.get(e.pointerId);
    if (!previo) return;
    const { r, w, h } = medidas();
    punteros.current.set(e.pointerId, { x: e.clientX, y: e.clientY });
    const ps = [...punteros.current.values()];
    if (ps.length >= 2) {
      const [a, b] = ps;
      const dist = Math.hypot(a.x - b.x, a.y - b.y);
      const medio = { x: (a.x + b.x) / 2 - r.left, y: (a.y + b.y) / 2 - r.top };
      if (pinza.current && pinza.current.dist > 0) {
        const t = zoomRef.current;
        const acercado = zoomEn(t, medio.x, medio.y, t.s * (dist / pinza.current.dist), w, h);
        aplicar(mover(acercado, medio.x - pinza.current.medio.x, medio.y - pinza.current.medio.y, w, h));
      }
      pinza.current = { dist, medio };
    } else if (zoomRef.current.s > 1) {
      aplicar(mover(zoomRef.current, e.clientX - previo.x, e.clientY - previo.y, w, h));
    }
  };

  const arriba = (e) => {
    punteros.current.delete(e.pointerId);
    pinza.current = null;
  };

  const dobleClic = (e) => {
    const { r, w, h } = medidas();
    const t = zoomRef.current;
    aplicar(t.s > 1 ? SIN_ZOOM : zoomEn(t, e.clientX - r.left, e.clientY - r.top, 2.5, w, h));
  };

  return (
    <div
      ref={marco}
      className={`relative h-full w-full overflow-hidden select-none ${zoom.s > 1 ? 'cursor-grab active:cursor-grabbing' : 'cursor-zoom-in'}`}
      style={{ touchAction: 'none' }}
      onPointerDown={abajo}
      onPointerMove={movido}
      onPointerUp={arriba}
      onPointerCancel={arriba}
      onDoubleClick={dobleClic}
      data-vivo-marco
    >
      <div
        className="h-full w-full origin-top-left will-change-transform"
        style={{ transform: `translate(${zoom.x}px, ${zoom.y}px) scale(${zoom.s})` }}
      >
        {children}
      </div>
    </div>
  );
};

export const ReproductorVivo = ({ identifier, nombre, onCerrar }) => {
  const video = useRef(null);
  const hls = useRef(null);
  const generacion = useRef(0);
  const timers = useRef([]);
  const reloj = useRef(null);
  const [estado, setEstado] = useState('pidiendo'); // pidiendo | despertando | reproduciendo | fin | error
  const [mensaje, setMensaje] = useState('');
  const [calidad, setCalidad] = useState(null);
  const [restan, setRestan] = useState(null);
  const [espera, setEspera] = useState(0);
  const [zoom, setZoom] = useState(SIN_ZOOM);

  const limpiarTimers = () => {
    timers.current.forEach(clearTimeout);
    timers.current = [];
    if (reloj.current) { clearInterval(reloj.current); reloj.current = null; }
  };

  const detener = useCallback(() => {
    generacion.current += 1;
    limpiarTimers();
    if (hls.current) { hls.current.destroy(); hls.current = null; }
    const v = video.current;
    if (v) { v.pause(); v.removeAttribute('src'); v.load(); }
  }, []);

  const terminar = useCallback((txt) => {
    detener();
    setRestan(null);
    setMensaje(txt || 'Terminaron los 2 minutos del vivo.');
    setEstado('fin');
  }, [detener]);

  const fallar = useCallback((txt, segundos = 0) => {
    detener();
    setRestan(null);
    setMensaje(txt);
    setEspera(segundos);
    setEstado('error');
  }, [detener]);

  const iniciar = useCallback(async (cal) => {
    detener();
    const gen = generacion.current;
    const vigente = () => gen === generacion.current;
    setEstado('pidiendo');
    setMensaje('');
    setEspera(0);
    setRestan(null);
    setCalidad(cal);
    let ses;
    try {
      ses = await pedirSesion({ identifier, calidad: cal });
    } catch (e) {
      if (vigente()) fallar(e.message, Number(e.datos?.espera) || 0);
      return;
    }
    if (!vigente()) return;
    setEstado('despertando');
    const t0 = Date.now();

    const reproducir = () => {
      const v = video.current;
      if (!v || !vigente()) return;
      let contando = false;
      v.onplaying = () => {
        if (contando || !vigente()) return;
        contando = true;
        setEstado('reproduciendo');
        let quedan = Number(ses.maxSegundos) || 120;
        setRestan(quedan);
        reloj.current = setInterval(() => {
          quedan -= 1;
          setRestan(quedan);
          if (quedan <= 0) terminar();
        }, 1000);
      };
      if (Hls.isSupported()) {
        let erroresMedia = 0;
        const h = new Hls({ manifestLoadingMaxRetry: 6, levelLoadingMaxRetry: 6, fragLoadingMaxRetry: 6, liveSyncDurationCount: 3 });
        hls.current = h;
        h.on(Hls.Events.ERROR, (_, d) => {
          if (!d.fatal || !vigente()) return;
          if (d.response?.code === 410) return terminar();
          if (d.type === Hls.ErrorTypes.MEDIA_ERROR && erroresMedia === 0) { erroresMedia += 1; h.recoverMediaError(); return; }
          if (cal === 'main') return iniciar('sub');   // el navegador no pudo con la 4K: la chica anda en todos lados
          fallar('Se cortó la señal de la cámara.');
        });
        h.loadSource(ses.hls);
        h.attachMedia(v);
      } else if (v.canPlayType('application/vnd.apple.mpegurl')) {
        v.src = ses.hls;                                 // iPhone/Safari: HLS nativo
        v.onerror = () => { if (vigente()) (cal === 'main' ? iniciar('sub') : fallar('Se cortó la señal de la cámara.')); };
      } else {
        fallar('Tu navegador no puede reproducir el vivo. Probá con Chrome, Edge o Safari.');
        return;
      }
      const reproduccion = v.play();               // en navegadores viejos play() no devuelve promesa
      if (reproduccion && reproduccion.catch) reproduccion.catch(() => {});
    };

    const esperar = async () => {
      if (!vigente()) return;
      const r = await consultarPlaylist(ses.hls);
      if (!vigente()) return;
      if (r.listo) return reproducir();
      if (r.status === 410) return fallar(r.mensaje || 'Terminó la sesión del vivo.');
      const pasado = Date.now() - t0;
      if (cal === 'main' && pasado > MAIN_HASTA_MS) return iniciar('sub');
      if (pasado > RENDIRSE_MS) {
        return fallar('La cámara no responde. Puede estar sin señal 4G, sin batería o apagada. Probá de nuevo en unos minutos.');
      }
      timers.current.push(setTimeout(esperar, POLL_MS));
    };
    esperar();
  }, [identifier, detener, fallar, terminar]);

  // arranca solo al abrir; se corta al cerrar, al cambiar de pestaña o al salir de la página (no gastar 4G)
  useEffect(() => {
    iniciar(soportaHevc() ? 'main' : 'sub');
    const oculta = () => { if (document.visibilityState === 'hidden') terminar('El vivo se pausó porque saliste de la página.'); };
    const tecla = (e) => { if (e.key === 'Escape') onCerrar(); };
    document.addEventListener('visibilitychange', oculta);
    window.addEventListener('pagehide', detener);
    window.addEventListener('keydown', tecla);
    const overflowPrevio = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      detener();
      document.removeEventListener('visibilitychange', oculta);
      window.removeEventListener('pagehide', detener);
      window.removeEventListener('keydown', tecla);
      document.body.style.overflow = overflowPrevio;
    };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps -- sólo al montar

  // cuenta regresiva del "descansando" antes de habilitar "Ver de nuevo"
  useEffect(() => {
    if (espera <= 0) return undefined;
    const t = setTimeout(() => setEspera((s) => s - 1), 1000);
    return () => clearTimeout(t);
  }, [espera]);

  const zoomBoton = (factor) => {
    const el = video.current?.parentElement?.parentElement;
    if (!el) return;
    const { width: w, height: h } = el.getBoundingClientRect();
    setZoom((t) => zoomEn(t, w / 2, h / 2, t.s * factor, w, h));
  };

  const verDeNuevo = () => { setZoom(SIN_ZOOM); iniciar(soportaHevc() ? 'main' : 'sub'); };

  return (
    <div className="fixed inset-0 z-[1000] flex flex-col bg-black text-white" role="dialog" aria-modal="true" aria-label="Cámara en vivo" data-vivo-reproductor>
      <div className="flex items-center gap-2 px-3 py-2 sm:px-4 bg-black/80">
        <span className="inline-flex items-center gap-1.5 rounded-full bg-red-600 px-2.5 py-1 text-xs font-extrabold tracking-wider">
          <span className="h-2 w-2 rounded-full bg-white" />EN VIVO
          <span className="rounded-full bg-white/25 px-1.5 py-0.5 text-[10px] font-semibold lowercase leading-none">beta</span>
        </span>
        <span className="truncate text-sm text-white/80">{nombre}</span>
        <span className="ml-auto flex items-center gap-2">
          {estado === 'reproduciendo' && calidad && (
            <span className="rounded bg-white/15 px-1.5 py-0.5 text-[11px] font-semibold">{calidad === 'main' ? '4K' : 'HD'}</span>
          )}
          {restan != null && <span className="font-mono text-sm tabular-nums" aria-label="Tiempo que queda">{formatear(restan)}</span>}
          <button type="button" onClick={onCerrar} className="rounded-full p-1.5 hover:bg-white/15" aria-label="Cerrar el vivo">
            <X size={22} />
          </button>
        </span>
      </div>

      <div className="relative flex-1 min-h-0">
        <MarcoZoom zoom={zoom} setZoom={setZoom}>
          <video ref={video} muted playsInline autoPlay className="h-full w-full object-contain pointer-events-none" />
        </MarcoZoom>

        {(estado === 'pidiendo' || estado === 'despertando') && (
          <div className="absolute inset-0 flex flex-col items-center justify-center gap-3 p-6 text-center pointer-events-none" data-vivo-estado={estado}>
            <Loader2 className="h-10 w-10 animate-spin text-white/80" />
            <p className="text-lg font-semibold">{estado === 'pidiendo' ? 'Conectando…' : 'Despertando la cámara…'}</p>
            <p className="max-w-sm text-sm text-white/70">Las cámaras 4G duermen para ahorrar batería y datos: el vivo puede tardar hasta un minuto en aparecer.</p>
          </div>
        )}

        {(estado === 'fin' || estado === 'error') && (
          <div className="absolute inset-0 flex flex-col items-center justify-center gap-4 bg-black/70 p-6 text-center" data-vivo-estado={estado}>
            <p className="max-w-md text-lg font-semibold">{mensaje}</p>
            <div className="flex gap-3">
              <button
                type="button"
                onClick={verDeNuevo}
                disabled={espera > 0}
                className="rounded-full bg-red-600 px-5 py-2.5 font-semibold hover:bg-red-700 disabled:opacity-50"
              >
                {espera > 0 ? `Ver de nuevo en ${espera} s` : 'Ver de nuevo'}
              </button>
              <button type="button" onClick={onCerrar} className="rounded-full bg-white/15 px-5 py-2.5 font-semibold hover:bg-white/25">Cerrar</button>
            </div>
          </div>
        )}
      </div>

      <div className="flex flex-wrap items-center justify-center gap-x-4 gap-y-2 px-3 py-2 bg-black/80 text-xs text-white/70">
        <span className="flex items-center gap-1">
          <button type="button" onClick={() => zoomBoton(1 / 1.5)} className="rounded-full p-1.5 hover:bg-white/15" aria-label="Alejar"><Minus size={18} /></button>
          <span className="w-10 text-center font-mono tabular-nums text-white">{zoom.s.toFixed(1)}×</span>
          <button type="button" onClick={() => zoomBoton(1.5)} disabled={zoom.s >= ZOOM_MAX} className="rounded-full p-1.5 hover:bg-white/15 disabled:opacity-40" aria-label="Acercar"><Plus size={18} /></button>
          <button type="button" onClick={() => setZoom(SIN_ZOOM)} disabled={zoom.s === 1} className="rounded-full p-1.5 hover:bg-white/15 disabled:opacity-40" aria-label="Ver completo"><RotateCcw size={16} /></button>
        </span>
        <span className="hidden sm:inline">Rueda del mouse para hacer zoom · arrastrá para moverte · doble clic para acercar</span>
        <span className="sm:hidden">Pellizcá con dos dedos para hacer zoom</span>
        <span>Beta · el vivo se corta a los 2 minutos</span>
      </div>
    </div>
  );
};

// Lo que se pone sobre el video del timelapse: el botón y, al tocarlo, el reproductor a pantalla completa.
const VivoBeta = ({ userdata, className = '' }) => {
  const [abierto, setAbierto] = useState(false);
  const identifier = userdata?.identifier;
  if (!identifier) return null;
  return (
    <>
      <div className={className}><BotonVivo onClick={() => setAbierto(true)} /></div>
      {abierto && createPortal(
        <ReproductorVivo identifier={identifier} nombre={userdata?.Name || userdata?.obra || ''} onCerrar={() => setAbierto(false)} />,
        document.body,
      )}
    </>
  );
};

export default VivoBeta;
